import api from "./base";

export const fetchBuildings = (searchTerm?: string, filters?: any) => {
  const url = searchTerm ? `buildings/` : "buildings/";
  const params: any = {};
  if (searchTerm) {
    params.search = searchTerm;
  }
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (!params[key]) params[key] = [];
        value.forEach((v) => params[key].push(v));
      } else if (value !== undefined && value !== null) {
        params[key] = value;
      }
    });
  }
  return api.get(url, { params }).then((res) => res.data);
};

export const fetchBuildingDetail = (id: string) =>
  api.get(`buildings/${id}/`).then((res) => res.data);

export const createBuilding = (data: {
  name: string;
  nyc_bin: string;
  portfolio?: string | null;
}) => api.post(`buildings/`, data).then((res) => res.data);

export const deleteBuilding = (id: string) =>
  api.delete(`buildings/${id}/`).then((res) => res.data);

export const addBuildingExternalId = (buildingId: string, data) =>
  api
    .post(`buildings/${buildingId}/external_ids/`, data)
    .then((res) => res.data);

export const removeBuildingExternalId = (id: string, externalId: string) =>
  api
    .delete(`buildings/${id}/external_ids/${externalId}/`)
    .then((res) => res.data);

export const updateBuildingName = (id: string, data: { name: string }) =>
  api.put(`buildings/${id}/`, data).then((res) => res.data);

export const markSubmissionsPaid = (buildingId: string, paymentIntent: any) =>
  api
    .post(`buildings/${buildingId}/submissions_paid/`, {
      payment_intent: paymentIntent,
    })
    .then((res) => res.data);

export const updateBuildingOwner = (data: {
  name: string;
  email: string;
  phone: string;
  ownerType: number;
  buildingId?: string;
  id?: string;
}) => {
  const payload = {
    name: data.name,
    email: data.email,
    phone: data.phone,
    owner_type: data.ownerType,
    building_id: data.buildingId,
    ...(data.id ? { id: data.id } : {}),
  };
  if (!data.id) {
    return api.post(`building-owner/`, payload).then((res) => res.data);
  }
  return api.put(`building-owner/${data.id}/`, payload).then((res) => res.data);
};

export const fetchBuildingOwners = () =>
  api.get(`building-owner/`).then((res) => res.data);

export const fetchBuildingBeamTickets = (buildingId: string) =>
  api.get(`buildings/${buildingId}/beam_tickets/`).then((res) => res.data);

/**
 * ECB violations summary for a single building.
 *
 * BE owns the underlying DOB ECB Violations dataset + the appeal-lag
 * `legalCaveat` copy (counsel-compliance agent). FE only renders.
 *
 * Endpoint:
 *   GET /api/tracking/portal/buildings/<id>/penalty-exposure/
 *
 * Wire shape (Phase C-2, post-OATH-merge):
 *
 *     {
 *       summary: {
 *         totalViolations: number;
 *         activeViolations: number;        // post-merge ECB ∪ OATH
 *         appealsInProgress: number;       // OATH stays
 *         resolvedViaOath: number;         // closed via OATH
 *         civilPenaltiesDue: number;       // dollars
 *         asOf: string;                    // ISO datetime
 *         legalCaveat: string;             // counsel-owned disclaimer
 *       };
 *       violations: Array<{
 *         // ... per-violation fields ...
 *         effectiveStatus:
 *           "ACTIVE"
 *           | "STAYED_PENDING_APPEAL"
 *           | "DISMISSED_AT_OATH"
 *           | "RESOLVED_VIA_OATH";
 *       }>;
 *     }
 *
 * Pre-Phase-C-2, the BE returns a flatter shape (no `summary` wrapper,
 * no `violations` array, no `appealsInProgress`/`resolvedViaOath`). The
 * query hook in `@/queries/queries.ts` adapts the legacy payload to the
 * post-C-2 shape so the component can read a single canonical type. See
 * `usePenaltyExposureQuery` for the normalization.
 *
 * Field names are camelCase here because axios's response interceptor
 * (`@/api/base`) runs `camelizeKeys` over every body. The BE emits
 * snake_case (`active_violations`, `effective_status`, etc.).
 */
export type EcbEffectiveStatus =
  | "ACTIVE"
  | "STAYED_PENDING_APPEAL"
  | "DISMISSED_AT_OATH"
  | "RESOLVED_VIA_OATH";

export interface EcbViolationItem {
  /** DOB violation id / ECB number — opaque to the FE. */
  violationId?: string;
  issuedAt?: string;
  hearingDate?: string;
  balanceDue?: number;
  /** Post-merge status across ECB and OATH. */
  effectiveStatus: EcbEffectiveStatus;
  /** Free-form description; not currently rendered in the v1 card. */
  description?: string;
}

/**
 * Sprint 2 / Stream D — DOB NOW Safety addendum status.
 *
 * Wire-contract enum from the BE serializer. Drives the card's
 * primary number + tooltip routing:
 *   - `ok`            : composite total = ECB + DOB NOW Safety; show
 *                       breakdown line.
 *   - `unavailable`   : Akamai/cookie/adapter failure; collapse to
 *                       ECB only with neutral tooltip.
 *   - `disabled`      : feature flag off; render as if DOB NOW didn't
 *                       exist (no chrome — don't shame ops for a
 *                       flag they didn't set).
 *   - `address_mismatch`: BIN/address verification failed; ECB-only
 *                       with stronger "verify address" tooltip.
 */
export type DobNowSafetyStatus =
  | "ok"
  | "unavailable"
  | "disabled"
  | "address_mismatch";

export interface EcbViolationsResponse {
  /**
   * Building's NYC BIN, mirrored from the BE response top level.
   * Used by the PenaltyExposureCard to mint per-section CTA deeplinks
   * (DOB BIS Property Profile / ECB-by-BIN / Boiler list).
   * Optional on the type because older BE deploys may not include it
   * — the card falls back to a generic search page when missing.
   */
  bin?: string;
  summary: {
    totalViolations: number;
    activeViolations: number;
    appealsInProgress: number;
    resolvedViaOath: number;
    civilPenaltiesDue: number;
    asOf: string;
    legalCaveat: string;
    /**
     * DOB NOW Safety total (Decimal as string, two-decimal format,
     * e.g. "94400.00"). Always present: penaltyExposure.components.dob_now
     * in every status, so `disabled` carries the figure too.
     */
    dobNowSafetyTotalDue?: string;
    dobNowSafetyRowCount?: number;
    dobNowSafetyStatus?: DobNowSafetyStatus;
    /**
     * Short routing string ("snapshot_missing", the one the backend writes
     * since 10-20; legacy: "akamai_blocked", "cookie_missing",
     * "address_mismatch", "adapter_unavailable", "unknown"). Set only
     * when status === "unavailable". The card uses it to choose
     * which tooltip copy to render.
     */
    dobNowSafetyUnavailableReason?: string | null;
    /**
     * ECB + DOB NOW Safety, two-decimal Decimal as string. The card's
     * primary dollar amount when status === "ok"; collapses to ECB-
     * only otherwise.
     */
    compositeTotalDue?: string;
  };
  violations: EcbViolationItem[];
  /**
   * Sprint 4 / F3 — additive `penaltyExposure` block. Optional on
   * the type because:
   *  (1) older BE deploys (before this PR) won't emit it, and the
   *      normalizer in queries.ts adapts to the legacy shape;
   *  (2) callers that read only `summary` / `violations` (the
   *      pre-F3 PenaltyExposureCard) shouldn't need to widen.
   *
   * The PenaltyExposureCard reads this block via the
   * `usePenaltyExposureQuery` hook (which returns the full
   * `EcbViolationsResponse`; consumers reach `.penaltyExposure` off
   * the result).
   */
  penaltyExposure?: PenaltyExposureResponse;
}

// ---------------------------------------------------------------------------
// Sprint 4 / F3 — penalty-exposure types.
//
// The BE emits these in snake_case; the axios response interceptor in
// `@/api/base` runs `camelizeKeys` over every body so we type the
// camelCase shape here. Decimal values arrive as strings from the BE
// (precision contract: never let JSON's lossy Number coercion eat
// $1,234,567.89 -> 1234567.89). The FE renders via `parseFloat` for
// `Intl.NumberFormat`; the string survives unchanged through axios.
//
// Failure-mode contract (mirrors the BE composer):
//   - When at least one of dob_now / ecb / boiler errored or is
//     flag-off, `isPartial=true` and `errors[]` lists the failing
//     components. OATH errors do NOT flip `isPartial` (V1.1 OATH is
//     fetched but not customer-visible; the B1 composer absorbs the
//     OATH-error case in the rendered hero number).
//   - Each component still appears in `components` even on failure;
//     `source: "error"` + `error: "<reason>"` describe the slot's
//     state. The FE renders a per-section "Couldn't refresh" pill
//     using these values.
// ---------------------------------------------------------------------------

export interface PenaltyExposureRow {
  /** ISO date "YYYY-MM-DD"; empty string when the source has no date. */
  date: string;
  /** Short agency / source label ("DOB", "ECB", "FDNY", "DSNY", etc.). */
  issuer: string;
  /** Free-form description. FE truncates to 60 chars with `title=`. */
  description: string;
  /** Decimal as string. Parse with `parseFloat` for `Intl.NumberFormat`. */
  amountUsd: string;
  /** Short status string for the colored pill. */
  status: string;
  /**
   * Wave-11 Track A — deterministic, building-scoped row identifier
   * stamped by the BE composer's `_enrich_row` and surviving
   * `_serialize_component_slot` to the FE wire. Shape:
   *   `${issuer_prefix}:${record_locator}`  (e.g. "ecb:12345678X")
   * with a deterministic sha1 fallback for rows that lack a locator.
   * Used by the row-chat icon (Track G) to echo the id back to the
   * `row_explain` BE tool — the FE never invents this value.
   * Optional in TS because wave-10 callers + legacy snapshots predate
   * the field; consumers MUST treat absence as "row is not chat-
   * addressable" (icon hidden / click no-op).
   */
  rowId?: string;
}

export type PenaltyExposureComponentSource =
  | "live"
  | "snapshot"
  | "cache"
  | "error"
  | "flag_off";

export interface PenaltyExposureComponent {
  /** Decimal as string — sum of contributing rows for this component. */
  totalUsd: string;
  /** Where the data came from. "error"/"flag_off" mean the slot failed. */
  source: PenaltyExposureComponentSource;
  /** Human-readable error string when `source` is "error". */
  error: string | null;
  /** ISO datetime string; null on `source: "error"` / `flag_off`. */
  fetchedAt: string | null;
  /** 5-column rows for the per-section table. May be empty. */
  rows: PenaltyExposureRow[];
}

export interface PenaltyExposureResponse {
  /**
   * Sum across visible components per the B1 formula:
   *   dob_now + max(ecb, oath_dob) + oath_other_agency + boiler
   * Decimal as string — the FE chooses `parseFloat` vs string-render
   * per surface (hero number parses; tooltips can render verbatim).
   */
  totalUsd: string;
  /**
   * True when one of the *visible* components (dob_now / ecb /
   * boiler) errored or is flag-off. OATH-only failures do NOT flip
   * this flag — see B1 composer §3.
   */
  isPartial: boolean;
  /**
   * `{component, error}[]` for any failing component. Includes OATH
   * failures for staff/debugging visibility even though they don't
   * drive `isPartial`.
   */
  errors: Array<{ component: string; error: string }>;
  /**
   * Always 5 keys present (even when a component errored):
   * `dobNow`, `ecb`, `boiler`, `oathDob`, `oathOtherAgency`. The v1
   * card renders the first three; the OATH slots are payload-only
   * and surface in the staff drill-in / v1.1.
   */
  components: {
    dobNow: PenaltyExposureComponent;
    ecb: PenaltyExposureComponent;
    boiler: PenaltyExposureComponent;
    oathDob: PenaltyExposureComponent;
    oathOtherAgency: PenaltyExposureComponent;
  };
}

export const fetchBuildingEcbViolations = (
  buildingId: string,
): Promise<EcbViolationsResponse> =>
  api
    .get(`tracking/portal/buildings/${buildingId}/penalty-exposure/`)
    .then((res) => res.data);

// Sprint 3 / Stream I — page-visit-fresh "Penalty Violations" hook.
//
// Wire-shape of the POST .../penalty-exposure/refresh/ endpoint.
//   * status === "enqueued" — Celery task fired; throttle now
//     locked for ``throttle_seconds``. The FE should poll the GET
//     endpoint until ``as_of`` advances.
//   * status === "throttled" — another refresh is in-flight (multi-
//     tab visit, browser back/forward, etc.). The FE should still
//     poll the GET — the pending refresh will land soon.
//   * 4xx — feature flag off, no BIN, RLS mismatch. The FE collapses
//     these to "skip refresh, show stale data with no fanfare".
export interface BuildingPenaltiesRefreshResponse {
  status: "enqueued" | "throttled";
  throttle_seconds: number;
  retry_after_seconds?: number;
}

/**
 * Fire a non-blocking ECB refresh for a building. Returns the
 * server's enqueue/throttle decision; never throws on 4xx — those
 * are signalled via a resolved promise with ``status: "skipped"``
 * so the caller can branch without try/catch on every call site.
 *
 * NOTE (Stream 4): kept around as the legacy / safety-net path —
 * the manual retry button on `PenaltyExposureCard` still calls this
 * helper. The page-visit-fresh hook now drives
 * ``triggerBuildingPenaltiesRefreshLongPoll`` instead.
 */
export const triggerBuildingPenaltiesRefresh = async (
  buildingId: string,
): Promise<BuildingPenaltiesRefreshResponse | { status: "skipped" }> => {
  try {
    const res = await api.post(
      `tracking/portal/buildings/${buildingId}/penalty-exposure/refresh/`,
    );
    return res.data as BuildingPenaltiesRefreshResponse;
  } catch (err: unknown) {
    // Any non-2xx — feature flag off (404), RLS mismatch (404), no
    // BIN (404), or transient network error — collapses to "skipped"
    // so the calling code only needs one branch. Polling continues
    // regardless because the server-side cron beat will eventually
    // refresh the snapshot even without our nudge.
    return { status: "skipped" };
  }
};

// ---------------------------------------------------------------------------
// Stream 4 — long-poll refresh helper.
//
// The BE accepts ``?wait=true`` on the same POST. Behavior:
//   * 200 ``{ status: "fresh", ...EcbViolationsResponse }`` — the
//     server long-polled (up to ~30s), the snapshot's ``fetched_at``
//     advanced, and the new payload is returned inline. Same shape
//     as ``GET /penalty-exposure/`` modulo the leading ``status`` key.
//     Single round-trip — the FE pushes this directly into the
//     TanStack cache and skips the GET. This is the win.
//   * 200 ``{ status: "still_refreshing", as_of: "<iso>" }`` — 30s
//     elapsed without snapshot advance. Caller may retry the long-
//     poll once; further retries fall through to cached data.
//   * 200 ``{ status: "throttled", ... }`` — should not happen with
//     ``?wait=true`` per the BE contract, but we tolerate it.
//   * 202 ``{ status: "enqueued", ... }`` — server semaphore was
//     saturated and the BE fell back to legacy enqueue. The new
//     payload is NOT inline; FE callers fall through to cached data.
//   * 404 — feature flag off / no BIN / RLS mismatch — collapse to
//     ``skipped`` like the legacy helper does.
//
// Axios timeout is set 5s above the server's 30s ceiling so a slow
// last byte from the BE doesn't trigger a client-side abort.
// ---------------------------------------------------------------------------

export type BuildingPenaltiesLongPollResponse =
  | ({ status: "fresh" } & EcbViolationsResponse)
  | { status: "still_refreshing"; asOf?: string | null }
  | {
      status: "throttled";
      throttleSeconds?: number;
      retryAfterSeconds?: number;
    }
  | {
      status: "enqueued";
      throttleSeconds?: number;
      retryAfterSeconds?: number;
    }
  | { status: "skipped" };

/**
 * Long-poll variant of the refresh endpoint. Single network call
 * resolves to either the fresh payload (success path) or a sentinel
 * ``status`` the caller can branch on. Never throws — 4xx / network
 * errors collapse to ``status: "skipped"`` so callers don't need a
 * try/catch.
 */
export const triggerBuildingPenaltiesRefreshLongPoll = async (
  buildingId: string,
): Promise<BuildingPenaltiesLongPollResponse> => {
  try {
    const res = await api.post(
      `tracking/portal/buildings/${buildingId}/penalty-exposure/refresh/`,
      undefined,
      {
        params: { wait: true },
        // 5s above the BE's 30s long-poll ceiling so a slow last
        // byte doesn't trigger a client-side abort.
        timeout: 35_000,
      },
    );
    // The axios response interceptor (see `api/base`) camelizes
    // every JSON key, so a BE payload of {"status": "fresh", ...}
    // arrives unchanged (`status` is already camelCase) but
    // `fetched_at` etc. inside become `fetchedAt`. The shape under
    // `status: "fresh"` is the camelCase EcbViolationsResponse.
    return res.data as BuildingPenaltiesLongPollResponse;
  } catch (err: unknown) {
    // Any non-2xx, axios `timeout` abort, network drop, etc.
    // collapses to `skipped` — caller falls back to cached data.
    return { status: "skipped" };
  }
};
